import React, { useState, useRef } from 'react';
import MapView from './components/MapView';
import OpenToPlayTabs from './OpenToPlayTabs';
import { useAuth } from './AuthContext';
import AuthForm from './AuthForm';
import ChatModal from './ChatModal';
import SessionList from './components/SessionList';
import FilterControls from './components/FilterControls';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './App.css';

import LoginModal from './components/LoginModal';

import PrivacyModal from './components/PrivacyModal';
import AboutModal from './components/AboutModal';
import HowToModal from './components/HowToModal';
import UserProfile from './components/UserProfile';
import EventsModal from './components/EventsModal';
import EditSessionModal from './components/EditSessionModal';
import ErrorBoundary from './components/ErrorBoundary';

function App() {
    const { user, logout } = useAuth();
    const [chatSession, setChatSession] = useState(null);
    const [editingSession, setEditingSession] = useState(null);
    const [activeFilter, setActiveFilter] = useState('all');
    const [searchRadius, setSearchRadius] = useState(50);
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [viewingProfileId, setViewingProfileId] = useState(null);
    const [showPrivacyModal, setShowPrivacyModal] = useState(false);
    const [showAboutModal, setShowAboutModal] = useState(false);
    const [showHowToModal, setShowHowToModal] = useState(false);
    const [showEventsModal, setShowEventsModal] = useState(false);
    const [userLocation, setUserLocation] = useState(null);
    const [mobileTab, setMobileTab] = useState('map');

    React.useEffect(() => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setUserLocation({
                        lat: position.coords.latitude,
                        lng: position.coords.longitude
                    });
                },
                (error) => {
                    // Error getting location

                }
            );
        }
    }, []);

    const mapViewRef = useRef();

    const handleViewOnMap = (session) => {
        if (mapViewRef.current && session.location) {
            mapViewRef.current.flyToLocation(session);
        }
    };

    const handleTriggerLogin = () => {
        setShowLoginModal(true);
    };

    const handleViewProfile = (uid) => {
        setViewingProfileId(uid);
    };

    return (
        <div className="App bg-gray-50 min-h-screen font-sans">
            <div className="max-w-6xl mx-auto px-2 lg:px-4 py-4 lg:py-8">
                <div className="flex justify-between items-center mb-6 bg-gray-50 lg:bg-transparent -mx-2 lg:mx-0 px-4 lg:px-0 py-2 lg:py-0 sticky top-0 z-40">
                    <h1 className="text-2xl lg:text-4xl font-bold text-indigo-600 tracking-tight flex items-center gap-2">
                        <img src="/logo.png" alt="Clackbase" className="h-8 lg:h-12 w-auto" />
                        CLACKBASE

                    </h1>

                    <div className="flex items-center gap-2 lg:gap-4">
                        {user ? (
                            <>
                                <div
                                    className="text-right cursor-pointer hover:opacity-80 transition-opacity"
                                    onClick={() => { setViewingProfileId(user.uid); }}
                                >
                                    <p className="text-sm text-gray-500 hidden sm:block">Welcome,</p>
                                    <p className="font-semibold text-gray-900 text-sm sm:text-base">{user.displayName || user.email}</p>
                                </div>
                                <img
                                    src={user.photoURL || `https://ui-avatars.com/api/?name=${user.displayName}`}
                                    alt="Avatar"
                                    className="w-8 h-8 lg:w-10 lg:h-10 rounded-full border border-gray-200 cursor-pointer hover:ring-2 hover:ring-indigo-300 transition-all"
                                    onClick={() => { setViewingProfileId(user.uid); }}
                                />
                                <button onClick={logout} className="text-sm bg-white border border-red-200 text-red-600 px-3 py-1 lg:px-4 lg:py-2 rounded-lg shadow-sm hover:bg-red-50 transition-colors">
                                    Logout
                                </button>
                            </>
                        ) : (
                            <button
                                onClick={() => setShowLoginModal(true)}
                                className="bg-indigo-600 text-white px-4 py-2 lg:px-6 lg:py-2 rounded-lg shadow hover:bg-indigo-700 font-semibold transition-colors whitespace-nowrap text-sm lg:text-base"
                            >
                                Login / Sign Up
                            </button>
                        )}
                    </div>
                </div>

                <ErrorBoundary>
                    <div className="flex flex-col lg:flex-row gap-8">
                        {/* List Column */}
                        <div className={`lg:w-1/3 flex-col gap-8 order-2 lg:order-1 ${mobileTab === 'list' ? 'flex' : 'hidden lg:flex'}`}>
                            <OpenToPlayTabs onTriggerLogin={handleTriggerLogin} />
                            <SessionList
                                onViewOnMap={(session) => {
                                    setMobileTab('map');
                                    handleViewOnMap(session);
                                }}
                                onOpenChat={setChatSession}
                                onTriggerLogin={handleTriggerLogin}
                                onOpenCalendar={() => setShowEventsModal(true)}
                                onEditSession={setEditingSession}
                                onViewProfile={handleViewProfile}
                                userLocation={userLocation}
                                searchRadius={searchRadius}
                            />
                        </div>
                        
                        {/* Map Column */}
                        <div className={`lg:w-2/3 order-1 lg:order-2 flex-col ${mobileTab === 'map' ? 'flex' : 'hidden lg:flex'}`}>
                            <FilterControls 
                                activeFilter={activeFilter} 
                                onFilterChange={setActiveFilter} 
                                searchRadius={searchRadius}
                                onRadiusChange={setSearchRadius}
                            />
                            <div className="rounded-lg overflow-hidden shadow-lg h-[calc(100vh-210px)] min-h-[400px] lg:h-[70vh] lg:min-h-[500px] relative">
                                <MapView
                                    ref={mapViewRef}
                                    onOpenChat={setChatSession}
                                    activeFilter={activeFilter}
                                    onTriggerLogin={handleTriggerLogin}
                                    onOpenCalendar={() => setShowEventsModal(true)}
                                    onEditSession={setEditingSession}
                                    onViewProfile={handleViewProfile}
                                    userLocation={userLocation}
                                    searchRadius={searchRadius}
                                />
                            </div>
                        </div>
                    </div>
                </ErrorBoundary>

                {editingSession && <EditSessionModal session={editingSession} onClose={() => setEditingSession(null)} />}
                {chatSession && <ChatModal session={chatSession} onClose={() => setChatSession(null)} />}
                {showLoginModal && !user && <LoginModal onClose={() => setShowLoginModal(false)} />}
                {viewingProfileId && <UserProfile userId={viewingProfileId} onClose={() => setViewingProfileId(null)} />}
                {showAboutModal && <AboutModal onClose={() => setShowAboutModal(false)} />}
                {showHowToModal && <HowToModal onClose={() => setShowHowToModal(false)} />}
                {showEventsModal && (
                    <EventsModal
                        onClose={() => setShowEventsModal(false)}
                        onViewOnMap={handleViewOnMap}
                        onOpenChat={(s) => {
                            setChatSession(s);
                            setShowEventsModal(false);
                        }}
                        onTriggerLogin={handleTriggerLogin}
                        userLocation={userLocation}
                    />
                )}
                {showPrivacyModal && <PrivacyModal onClose={() => setShowPrivacyModal(false)} />}
                <ToastContainer position="top-right" autoClose={5000} />
            </div>

            <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-[100] flex justify-around items-center pt-2 pb-6 px-2 shadow-[0_-4px_10px_-1px_rgba(0,0,0,0.05)]">
                <button
                    onClick={() => { setMobileTab('map'); window.scrollTo(0,0); }}
                    className={`flex flex-col items-center p-2 w-full rounded-lg transition-colors ${mobileTab === 'map' ? 'text-indigo-600 font-bold scale-105' : 'text-gray-500 hover:text-gray-900'}`}
                >
                    <span className="text-2xl mb-1 drop-shadow-sm">🗺️</span>
                    <span className="text-[10px] tracking-wider uppercase">Map</span>
                </button>
                <div className="w-[1px] h-8 bg-gray-100"></div>
                <button
                    onClick={() => { setMobileTab('list'); window.scrollTo(0,0); }}
                    className={`flex flex-col items-center p-2 w-full rounded-lg transition-colors ${mobileTab === 'list' ? 'text-indigo-600 font-bold scale-105' : 'text-gray-500 hover:text-gray-900'}`}
                >
                    <span className="text-2xl mb-1 drop-shadow-sm">📋</span>
                    <span className="text-[10px] tracking-wider uppercase">List</span>
                </button>
                <div className="w-[1px] h-8 bg-gray-100"></div>
                <button
                    onClick={() => {
                        if (user) {
                            setViewingProfileId(user.uid);
                        } else {
                            setShowLoginModal(true);
                        }
                    }}
                    className={`flex flex-col items-center p-2 w-full rounded-lg transition-colors text-gray-500 hover:text-gray-900`}
                >
                    <span className="text-2xl mb-1 drop-shadow-sm">{user ? '👤' : '🔐'}</span>
                    <span className="text-[10px] font-bold tracking-wider uppercase">{user ? 'Profile' : 'Login'}</span>
                </button>
            </div>

            <footer className="mt-12 py-8 text-center text-gray-500 text-sm border-t border-gray-200 bg-white pb-32 lg:pb-8">
                <p>© {new Date().getFullYear()} Clackbase. <br className="sm:hidden" />Built with <span className="text-red-500">❤️</span> for the Chess Community.</p>
                <div className="flex flex-wrap justify-center items-center gap-x-4 gap-y-2 mt-3 px-4">
                    <button onClick={() => setShowHowToModal(true)} className="hover:text-indigo-600 transition-colors font-medium">Help</button>
                    <span className="text-gray-300">•</span>
                    <button onClick={() => setShowAboutModal(true)} className="hover:text-indigo-600 transition-colors font-medium">About</button>
                    <span className="text-gray-300">•</span>
                    <button onClick={() => setShowPrivacyModal(true)} className="hover:text-indigo-600 transition-colors font-medium">Privacy</button>
                    <span className="text-gray-300">•</span>
                    <a href="mailto:support@clackbase.com" className="hover:text-indigo-600 transition-colors font-medium">Feedback</a>
                    <span className="text-gray-300">•</span>
                    <a
                        href="https://buy.stripe.com/dRmcN67J86hq8PtfLT6c000"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-indigo-600 transition-colors font-bold flex items-center gap-1"
                    >
                        Donate
                    </a>
                </div>
            </footer>
        </div>
    );
}

export default App;

